/**
 * Toast Component
 *
 * Unified toast notification system for admin and frontend.
 * Replaces duplicate addNotice/showToast implementations.
 *
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';

/**
 * Toast configuration
 */
const DEFAULTS = {
    duration: 4000,
    position: 'bottom-center',
    dismissible: true
};

const ICONS = {
    success: '✅',
    error: '❌',
    warning: '⚠️',
    info: 'ℹ️'
};

let container = null;

/**
 * Initialize or get toast container.
 * @returns {HTMLElement}
 */
function getContainer() {
    if (container && document.body.contains(container)) {
        return container;
    }

    container = el('div', {
        class: 'presszone-comments-toast-container',
        'aria-live': 'polite',
        'aria-atomic': 'true',
        role: 'region',
        'aria-label': __('Notifications', 'comments-press-zone')
    });
    document.body.appendChild(container);
    return container;
}

/**
 * Show a toast notification.
 *
 * @param {Object|string} options - Toast options or message string
 * @param {string} options.message - Toast message
 * @param {string} [options.type='success'] - Type: success, error, warning, info
 * @param {number} [options.duration=4000] - Auto-dismiss duration (ms), 0 for persistent
 * @param {boolean} [options.dismissible=true] - Show dismiss button
 * @param {string} [options.icon] - Custom icon (overrides type icon)
 * @returns {HTMLElement} Toast element (for programmatic control)
 */
export function show(options) {
    // Allow simple string usage: Toast.show('Message')
    if (typeof options === 'string') {
        options = { message: options };
    }

    const {
        message,
        type = 'success',
        duration = DEFAULTS.duration,
        dismissible = DEFAULTS.dismissible,
        icon = ICONS[type] || ICONS.success
    } = options;

    const toastContainer = getContainer();

    const toast = el('div', {
        class: `presszone-comments-toast presszone-comments-toast--${type}`,
        role: 'alert'
    },
        el('span', { class: 'presszone-comments-toast__icon', 'aria-hidden': 'true' }, icon),
        el('span', { class: 'presszone-comments-toast__message' }, message),
        dismissible ? el('button', {
            class: 'presszone-comments-toast__dismiss',
            'aria-label': __('Dismiss', 'comments-press-zone'),
            type: 'button',
            onclick: () => dismiss(toast)
        }, '×') : null
    );

    toastContainer.appendChild(toast);

    // Auto-dismiss
    if (duration > 0) {
        toast._dismissTimeout = setTimeout(() => dismiss(toast), duration);
    }

    return toast;
}

/**
 * Dismiss a toast with animation.
 * @param {HTMLElement} toast
 */
export function dismiss(toast) {
    if (!toast || toast._dismissing) return;

    toast._dismissing = true;

    if (toast._dismissTimeout) {
        clearTimeout(toast._dismissTimeout);
    }

    toast.classList.add('presszone-comments-toast--hiding');

    // Remove after animation completes
    setTimeout(() => toast.remove(), 300);
}

/**
 * Shorthand methods
 */
export function success(message, options = {}) {
    return show({ ...options, message, type: 'success' });
}

export function error(message, options = {}) {
    return show({ ...options, message, type: 'error' });
}

export function warning(message, options = {}) {
    return show({ ...options, message, type: 'warning' });
}

export function info(message, options = {}) {
    return show({ ...options, message, type: 'info' });
}

/**
 * Clear all toasts
 */
export function clearAll() {
    const toastContainer = getContainer();
    [...toastContainer.children].forEach(toast => dismiss(toast));
}

// Default export as object for convenient usage
const Toast = { show, dismiss, success, error, warning, info, clearAll };
export default Toast;
